import { useLocation } from "react-router-dom";
import {
  useGetCategoryLocationListQuery,
  useGetLocationListQuery,
} from "../../../../../api/locations/locationsApiSlice";
import { CategoryNotice } from "../../../../../api/apartment/categoryOutcome";
import { LocationPros } from "../../../../../types/baseTypes";
import { categoryFromSearch } from "../../../../home/homeTiles";

export interface StationLocationList {
  data: LocationPros[] | undefined;
  /** What the server said about the URL's category (archived / unknown); null otherwise. */
  categoryNotice: CategoryNotice | null;
  /** The category the list is narrowed to, as read from the URL; undefined = none. */
  productCategory: string | undefined;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

/**
 * The location list of a station flow (sup, rental, storage) — #3429 step 3.
 *
 * Entered from a category tile (`?category=` in the URL, read with the home
 * tiles' own parser), the list shows only the locations that offer that
 * category (PO 2026-10-30) and carries the server's notice. Without a
 * category it is exactly today's request, sharing its cache entry with every
 * other reader of the plain list.
 *
 * Both queries are always declared and one is skipped, so the hooks run in
 * the same order on every render.
 */
export const useStationLocationList = (service: string): StationLocationList => {
  const { search } = useLocation();
  const productCategory = categoryFromSearch(search);

  const plain = useGetLocationListQuery(service, { skip: Boolean(productCategory) });
  const narrowed = useGetCategoryLocationListQuery(
    { services: service, productCategory: productCategory ?? "" },
    { skip: !productCategory }
  );

  if (productCategory) {
    return {
      data: Array.isArray(narrowed.data?.items) ? narrowed.data?.items : undefined,
      categoryNotice: narrowed.data?.categoryNotice ?? null,
      productCategory,
      isLoading: narrowed.isLoading,
      isError: narrowed.isError,
      isSuccess: narrowed.isSuccess,
    };
  }
  return {
    data: Array.isArray(plain.data) ? plain.data : undefined,
    categoryNotice: null,
    productCategory: undefined,
    isLoading: plain.isLoading,
    isError: plain.isError,
    isSuccess: plain.isSuccess,
  };
};
